import { useEffect, useState, useCallback } from 'react'
import { CommentAnchorType } from '@marketing-milk/interfaces'
import { CommentAnchor } from 'components/comments/CommentThread'

const KNOWN_TYPES: CommentAnchorType[] = [
  'section',
  'narrative-block',
  'performance-metric',
  'approval-row',
  'creative',
]

export const useCommentAnchors = (rootRef: React.RefObject<HTMLElement>) => {
  const [anchor, setAnchor] = useState<CommentAnchor | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      // Only open when the user explicitly clicks the comment trigger.
      const trigger = target.closest<HTMLElement>('[data-comment-trigger="true"]')
      if (!trigger) return
      const host = trigger.closest<HTMLElement>('[data-comment-anchor-type]')
      if (!host) return
      const type = host.getAttribute('data-comment-anchor-type') as CommentAnchorType | null
      const id = host.getAttribute('data-comment-anchor-id')
      const label = host.getAttribute('data-comment-label') ?? undefined
      if (!type || !id || !KNOWN_TYPES.includes(type)) return
      e.preventDefault()
      e.stopPropagation()
      setAnchor({ type, id, label })
      setOpen(true)
    }

    root.addEventListener('click', onClick)
    return () => root.removeEventListener('click', onClick)
  }, [rootRef])

  const close = useCallback(() => {
    setOpen(false)
    setAnchor(null)
  }, [])

  return { anchor, open, close, setAnchor, setOpen }
}
